import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import {
  Warning as WarningIcon,
  Send as SendIcon,
  AccountCircle as AccountCircleIcon,
} from '@mui/icons-material';
import API_BASE_URL from '../config';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const ContactContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const WarningPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 2,
  background: `linear-gradient(135deg, ${alpha(theme.palette.warning.light, 0.1)}, ${alpha(theme.palette.warning.main, 0.05)})`,
  border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
  backdropFilter: 'blur(10px)',
}));

const ContactPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 2,
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius * 2,
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  '&:hover': {
    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
  },
}));

const ContactUs = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    uid: '',
    email: '',
    reason: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    // Get UID from localStorage if available
    const storedUid = localStorage.getItem('uid');
    if (storedUid) {
      setFormData(prev => ({ ...prev, uid: storedUid }));
    }
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/reactivation-request`, formData, {
        withCredentials: true,
      });

      setSnackbar({
        open: true,
        message: 'Your reactivation request has been submitted successfully. We will contact you shortly.',
        severity: 'success',
      });

      // Clear form
      setFormData({
        uid: formData.uid, // Keep the UID
        email: '',
        reason: '',
        message: '',
      });

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to send reactivation request',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContactContainer maxWidth="md">
      <Box mb={6} textAlign="center">
        <AccountCircleIcon sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} />
        <Typography
          variant="h3"
          gutterBottom
          sx={{
            fontWeight: 700,
            background: (theme) =>
              `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.error.main})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
          }}
        >
          Account Reactivation Request
        </Typography>
      </Box>

      <WarningPaper elevation={0}>
        <Box display="flex" alignItems="center" gap={2}>
          <WarningIcon color="warning" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h6" color="warning.main" gutterBottom>
              Account Inactive
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your account has been deactivated due to policy violations or security concerns. 
              Please fill out this form to request reactivation. Our admin team will review your request 
              and contact you shortly.
            </Typography>
          </Box>
        </Box>
      </WarningPaper>

      <ContactPaper elevation={0}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="User ID"
                name="uid"
                value={formData.uid}
                onChange={handleChange}
                required
                disabled={!!localStorage.getItem('uid')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Reason for Deactivation"
                name="reason"
                select
                SelectProps={{
                  native: true,
                }}
                value={formData.reason}
                onChange={handleChange}
                required
              >
                <option value="">Select a reason</option>
                <option value="policy_violation">Policy Violation</option>
                <option value="security_concern">Security Concern</option>
                <option value="payment_issue">Payment Issue</option>
                <option value="other">Other</option>
              </StyledTextField>
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Additional Information"
                name="message"
                multiline
                rows={4}
                value={formData.message}
                onChange={handleChange}
                required
                placeholder="Please provide any additional information that might help us process your reactivation request..."
              />
            </Grid>
            <Grid item xs={12}>
              <SubmitButton
                fullWidth
                variant="contained"
                type="submit"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
              >
                {loading ? 'Submitting Request...' : 'Submit Reactivation Request'}
              </SubmitButton>
            </Grid>
          </Grid>
        </form>

        <Divider sx={{ my: 4 }} />

        <Box textAlign="center">
          <Typography variant="body2" color="text.secondary">
            For urgent inquiries, please contact our support team at{' '}
            <Typography component="span" color="primary.main" sx={{ fontWeight: 600 }}>
              support@goldpetals.com
            </Typography>
          </Typography>
        </Box>
      </ContactPaper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ContactContainer>
  );
};

export default ContactUs;
